import { useEffect, useState, type ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { fetchAppeal, decideAppeal, type AdminAppeal } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { InfoRow } from '@/components/DataDisplay'
import { StatusBadge } from '@/components/StatusBadge'
import { ErrorState, PermissionDenied, Skeleton } from '@/components/States'
import { useAuth } from '@/providers/AuthProvider'
import { useToast } from '@/providers/UiProviders'

/**
 * A8.11/12 — Apellyatsiya kartasi + qaror. GET /admin/appeals/:id, POST
 * /admin/appeals/:id/decide (backend `548626f`). GRANT ijroni bekor qiladi
 * (ban=>unban, strike=>muddati tugaydi). Serverда audit.
 */
export function AppealCard() {
  const t = useT()
  const { can } = useAuth()
  const { toast } = useToast()
  const navigate = useNavigate()
  const { id = '' } = useParams()
  const [appeal, setAppeal] = useState<AdminAppeal | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchAppeal(Number(id), ac.signal)
      .then((a) => {
        if (alive) setAppeal(a)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [id, reload])

  if (!can(PERMISSIONS.USERS_VIEW)) return <PermissionDenied permission={PERMISSIONS.USERS_VIEW} />

  const decide = async (grant: boolean) => {
    setBusy(true)
    try {
      await decideAppeal(Number(id), grant, note)
      toast(grant ? t.appeals.grantedToast : t.appeals.rejectedToast, { tone: 'success' })
      navigate('/appeals')
    } catch (e) {
      toast(t.appeals.decideFailed, {
        tone: 'error',
        ...(e instanceof Error && e.message ? { description: e.message } : {}),
      })
      setBusy(false)
    }
  }

  return (
    <>
      <PageHeader
        title={appeal ? `#${appeal.id} · ${appeal.kind}` : t.appeals.card}
        screenId="A8.11"
        actions={
          <Link
            to="/appeals"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.appeals.queue}
          </Link>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {loading ? (
          <div className="max-w-xl space-y-2 rounded-lg border border-border-default bg-surface-2 p-4">
            {Array.from({ length: 5 }, (_, i) => (
              <Skeleton key={i} className="h-4 w-2/3" />
            ))}
          </div>
        ) : error ? (
          <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
        ) : !appeal ? (
          <ErrorState title={t.states.notFoundTitle} />
        ) : (
          <div className="max-w-xl space-y-3">
            <div className="rounded-lg border border-border-default bg-surface-2 p-4">
              <div className="mb-3">
                <StatusBadge
                  status={appeal.status === 'pending' ? 'pending' : appeal.status === 'granted' ? 'active' : 'deleted'}
                />
              </div>
              <Field label={t.appeals.kind}>{appeal.kind === 'ban' ? t.appeals.kindBan : t.appeals.kindStrike}</Field>
              <Field label={t.col.name}>
                <Link to={`/users/${appeal.user_id}`} className="text-accent hover:underline">
                  {appeal.username ? `@${appeal.username}` : `#${appeal.user_id}`}
                </Link>
              </Field>
              {appeal.strike_id ? (
                <Field label={t.strikes.title}>
                  <Link to={`/users/${appeal.user_id}/strikes`} className="text-accent hover:underline">
                    #{appeal.strike_id}
                  </Link>
                </Field>
              ) : null}
              <Field label={t.mod.reason}>{appeal.reason}</Field>
              {appeal.status !== 'pending' && (
                <Field label={t.appeals.decisionNote}>{appeal.decision_note || '—'}</Field>
              )}
            </div>

            {appeal.status === 'pending' && can(PERMISSIONS.USERS_BAN) && (
              <div className="rounded-lg border border-border-default bg-surface-2 p-4">
                <div className="mb-2 text-sm font-medium text-secondary">{t.appeals.decision}</div>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={t.appeals.notePlaceholder}
                  rows={2}
                  className="mb-3 w-full rounded-md border border-border-default bg-surface-1 px-2.5 py-1.5 text-sm text-primary"
                />
                <div className="flex gap-2">
                  <button
                    disabled={busy}
                    onClick={() => decide(false)}
                    className="rounded-md border border-border-default bg-surface-1 px-3 py-1.5 text-sm text-secondary hover:text-primary disabled:opacity-50"
                  >
                    {t.appeals.reject}
                  </button>
                  <button
                    disabled={busy}
                    onClick={() => decide(true)}
                    className="rounded-md border border-success bg-success-subtle px-3 py-1.5 text-sm text-success hover:bg-success hover:text-white disabled:opacity-50"
                  >
                    {t.appeals.grant}
                  </button>
                </div>
                <p className="mt-2 text-xs text-tertiary">{t.appeals.grantHint}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <InfoRow label={label}>{children}</InfoRow>
}
